// client/src/pages/offers/Offers.js
//
// Offers, from both sides of the table.
//
// A student sees what they have been offered and answers it. A recruiter or
// placement officer sees what has been raised and can withdraw one, with a
// reason. The same list serves both because the server already scopes it — a
// student only ever receives their own rows.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  PageShell,
  SkeletonCard,
  StatStrip,
  StatTile,
  StatusBadge,
  Badge,
  Textarea
} from '../../components/ui';
import {
  BanknotesIcon,
  BriefcaseIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import placementService from '../../services/placement';
import openProtectedFile from '../../utils/openProtectedFile';

// Indian salary conventions: a package is spoken about in lakhs, and the digit
// grouping is 2-2-3 rather than 3-3-3. `en-IN` gets the grouping right; the
// lakh figure is what a student actually compares offers on.
const formatCtc = (value, currency = 'INR') => {
  if (value === null || value === undefined) return 'Not disclosed';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not disclosed';
  const grouped = n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  if (currency !== 'INR') return `${currency} ${grouped}`;
  const lakhs = n / 100000;
  return `₹${grouped}${lakhs >= 1 ? ` · ${Number(lakhs.toFixed(2))} LPA` : ''}`;
};

/** Tier names whose floor this CTC clears, highest last. */
const tierFor = (ctc, tiers) => {
  const list = (Array.isArray(tiers) ? tiers : [])
    .filter((t) => t && t.name)
    .map((t) => ({ name: t.name, minCtc: Number(t.minCtc) || 0 }))
    .sort((a, b) => a.minCtc - b.minCtc);
  let found = null;
  list.forEach((t) => {
    if (Number(ctc) >= t.minCtc) found = t;
  });
  return { tier: found, next: found ? list[list.indexOf(found) + 1] : list[0] };
};

/**
 * What accepting this offer will change for the student, in their placement
 * cell's own rules. Accepting is final, and a student who learns afterwards
 * that it closed every other drive has no way back.
 */
const acceptConsequences = (offer, policy) => {
  const lines = [];
  if (!policy?.enabled) return lines;
  if (policy.blockAfterAcceptance) {
    lines.push('Your placement cell closes further job applications once you accept an offer. Internships are not affected.');
  }
  if (policy.placedRule === 'higher_tier' && Array.isArray(policy.tiers) && policy.tiers.length > 1) {
    const { tier, next } = tierFor(offer.ctc, policy.tiers);
    if (tier && next) {
      lines.push(
        `This is a ${tier.name} offer. While you hold it you can only apply to ${next.name} roles (from ${formatCtc(next.minCtc)}) or higher.`
      );
    } else if (tier) {
      lines.push(`This is a ${tier.name} offer, the highest tier your placement cell uses.`);
    }
  } else if (policy.placedRule === 'ctc_multiplier' && Number(policy.ctcMultiplier) > 0 && offer.ctc) {
    lines.push(
      `While you hold it you can only apply to roles paying at least ${formatCtc(Number(offer.ctc) * Number(policy.ctcMultiplier))}.`
    );
  }
  if (policy.maxOffers) {
    lines.push(`Your placement cell allows at most ${policy.maxOffers} offer${policy.maxOffers === 1 ? '' : 's'} per student.`);
  }
  return lines;
};

const Offers = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';

  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [reason, setReason] = useState('');
  // The answer the student is about to give, held until they confirm it.
  const [confirming, setConfirming] = useState(null);
  // Verifying a letter counts the student as placed and locks the letter;
  // unverifying undoes that. Both were one stray tap away.
  const [verifying, setVerifying] = useState(null);
  const [policy, setPolicy] = useState(null);
  const isCell = user?.role === 'tpo' || user?.role === 'admin';

  // The signed offer letter: uploaded by the student or the company, checked
  // by the placement cell. NAAC asks for it behind every reported placement.
  const uploadLetter = (offer) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setBusyId(offer.id);
      try {
        await placementService.uploadOfferLetter(offer.id, file);
        toast.success('Offer letter uploaded');
        load();
      } catch {
        // api toasts
      } finally {
        setBusyId(null);
      }
    };
    input.click();
  };

  const verifyLetter = async (offer, verified) => {
    setBusyId(offer.id);
    try {
      await placementService.verifyOfferLetter(offer.id, verified);
      toast.success(verified ? 'Offer letter verified' : 'Verification removed');
      setVerifying(null);
      load();
    } catch {
      // api toasts
    } finally {
      setBusyId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, statsBody, policyBody] = await Promise.all([
        api.get('/offers', { params: { limit: 100 }, silent: true }),
        isStudent
          ? Promise.resolve(null)
          : api.get('/offers/stats', { silent: true }).catch(() => null),
        // The student's own institution's rules, to explain what accepting
        // does. Optional: without it the confirmation is just less specific.
        isStudent
          ? api.get('/placement/policy', { silent: true }).catch(() => null)
          : Promise.resolve(null)
      ]);
      setOffers(list.offers || []);
      setStats(statsBody?.stats || null);
      setPolicy(policyBody?.policy || null);
    } catch (err) {
      setError(err?.message || 'Could not load offers.');
    } finally {
      setLoading(false);
    }
  }, [isStudent]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (offer, action) => {
    setBusyId(offer.id);
    try {
      await api.patch(`/offers/${offer.id}/respond`, { action }, { silent: true });
      toast.success(action === 'accept' ? 'Offer accepted' : 'Offer declined');
      setConfirming(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not record your answer. Check your connection and try again.');
      // Already answered elsewhere (another tab, a second tap): show the
      // current state instead of leaving stale buttons on screen.
      if (err.status === 409) {
        setConfirming(null);
        load();
      }
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async () => {
    setBusyId(revoking.id);
    try {
      await api.patch(`/offers/${revoking.id}/revoke`, { reason }, { silent: true });
      toast.success('Offer withdrawn');
      setRevoking(null);
      setReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not withdraw the offer');
    } finally {
      setBusyId(null);
    }
  };

  const live = useMemo(
    () => offers.filter((o) => o.status === 'offered' || o.status === 'accepted'),
    [offers]
  );

  if (loading) {
    return (
      <PageShell>
        <PageHeader eyebrow="Offers" title="Offers" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="Offers"
        title={isStudent ? 'My offers' : 'Offers raised'}
        lead={
          isStudent
            ? 'Every offer made to you, and what you have said about it.'
            : 'Packages raised against applications, and their outcomes.'
        }
      />

      {!isStudent && stats && (
        <StatStrip className="mb-8">
          <StatTile label="Live offers" value={stats.total} hint="Outstanding and accepted" />
          <StatTile
            label="Median package"
            value={stats.medianCtc === null ? '—' : formatCtc(stats.medianCtc)}
            hint={
              stats.withCtc === 0
                ? 'No packages recorded yet'
                : `From ${stats.withCtc} offer${stats.withCtc === 1 ? '' : 's'} with a package`
            }
          />
          <StatTile
            label="Highest"
            value={stats.highestCtc === null ? '—' : formatCtc(stats.highestCtc)}
          />
          <StatTile label="Accepted" value={stats.accepted} hint={`${stats.ppo} PPO`} />
        </StatStrip>
      )}

      {error ? (
        <ErrorState title="Could not load offers" description={error} onRetry={load} />
      ) : offers.length === 0 ? (
        <EmptyState
          icon={BanknotesIcon}
          title={isStudent ? 'No offers yet' : 'No offers raised yet'}
          description={
            isStudent
              ? 'When a company makes you an offer it will appear here, with the package and your response.'
              : 'Raise an offer from an application once a candidate has been selected.'
          }
          action={
            isStudent ? (
              <Button as={Link} to="/jobs">
                Browse jobs
              </Button>
            ) : (
              <Button as={Link} to="/applications">
                Go to applications
              </Button>
            )
          }
        />
      ) : (
        <ul className="space-y-3">
          {offers.map((offer) => (
            <li key={offer.id}>
              <Card className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <Avatar
                    src={offer.organization?.logoUrl}
                    name={offer.organization?.name || 'Company'}
                    size="md"
                  />
                  <div className="min-w-0">
                    <h2 className="font-display text-base font-bold text-ink-950">
                      {offer.roleTitle || offer.job?.title}
                    </h2>
                    <p className="truncate text-sm text-ink-600">
                      {offer.organization?.name}
                      {offer.location ? ` · ${offer.location}` : ''}
                      {offer.isPPO ? ' · PPO' : ''}
                    </p>
                    {!isStudent && offer.student && (
                      <p className="truncate text-xs text-ink-500">
                        {offer.student.firstName} {offer.student.lastName}
                        {offer.student.studentProfile?.studentId
                          ? ` · ${offer.student.studentProfile.studentId}`
                          : ''}
                      </p>
                    )}
                    <p className="mt-1.5 font-semibold text-ink-950">
                      {formatCtc(offer.ctc, offer.currency)}
                    </p>
                    {['offered', 'accepted'].includes(offer.status) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        {offer.offerLetterUrl ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openProtectedFile(offer.offerLetterUrl)}
                              className="inline-flex min-h-[28px] items-center gap-1 font-medium text-azure-700 hover:underline"
                            >
                              <DocumentTextIcon aria-hidden="true" className="h-3.5 w-3.5" /> Offer letter
                            </button>
                            <Badge tone={offer.letterVerified ? 'success' : 'warning'}>
                              {offer.letterVerified ? 'Verified by placement cell' : 'Awaiting verification'}
                            </Badge>
                          </>
                        ) : (
                          <span className="text-ink-500">No offer letter uploaded yet</span>
                        )}
                      </div>
                    )}
                    {isStudent && offer.applicationId && (
                      <Link
                        to={`/applications/${offer.applicationId}`}
                        className="mt-1 inline-flex min-h-[28px] items-center text-xs font-semibold text-ink-950 hover:underline"
                      >
                        View application →
                      </Link>
                    )}
                    {offer.status === 'revoked' && offer.revokedReason && (
                      <p className="mt-1 text-sm text-ink-700">
                        Withdrawn: {offer.revokedReason}
                      </p>
                    )}
                  </div>
                </div>

                {/* Full width under the details on a phone: `shrink-0` kept
                    this row at its natural width, which ran off a 390px screen. */}
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                  <StatusBadge status={offer.status} />
                  {isStudent && offer.status === 'offered' && (
                    <>
                      <Button
                        size="sm"
                        variant="success"
                        disabled={busyId === offer.id}
                        onClick={() => setConfirming({ offer, action: 'accept' })}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === offer.id}
                        onClick={() => setConfirming({ offer, action: 'decline' })}
                      >
                        Decline
                      </Button>
                    </>
                  )}
                  {/* A verified letter is the record behind a placement: only the
                      cell may change it (the server refuses anyone else). */}
                  {['offered', 'accepted'].includes(offer.status) &&
                    !(isCell && offer.offerLetterUrl) &&
                    !offer.letterVerified && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={DocumentArrowUpIcon}
                      loading={busyId === offer.id}
                      onClick={() => uploadLetter(offer)}
                    >
                      {offer.offerLetterUrl ? 'Replace letter' : 'Upload letter'}
                    </Button>
                  )}
                  {isCell && offer.offerLetterUrl && (
                    <Button
                      size="sm"
                      variant={offer.letterVerified ? 'secondary' : 'success'}
                      icon={ShieldCheckIcon}
                      loading={busyId === offer.id}
                      onClick={() => setVerifying({ offer, verified: !offer.letterVerified })}
                    >
                      {offer.letterVerified ? 'Unverify' : 'Verify letter'}
                    </Button>
                  )}
                  {!isStudent && offer.status !== 'revoked' && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        setReason('');
                        setRevoking(offer);
                      }}
                    >
                      Withdraw
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {isStudent && live.length > 1 && (
        <Card className="mt-6 border-saffron-500/40 bg-saffron-50">
          <div className="flex gap-3">
            <BriefcaseIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-saffron-800" />
            <p className="text-sm text-ink-700">
              You are holding {live.length} offers. Most placement cells allow only one — check
              your institution&rsquo;s policy before accepting a second.
            </p>
          </div>
        </Card>
      )}

      <Modal
        open={Boolean(verifying)}
        onClose={() => setVerifying(null)}
        size="sm"
        title={verifying?.verified ? 'Verify this offer letter?' : 'Remove verification?'}
        description={
          verifying
            ? `${[verifying.offer.student?.firstName, verifying.offer.student?.lastName].filter(Boolean).join(' ') || 'Student'}: ${
                verifying.offer.roleTitle || verifying.offer.job?.title || 'offer'
              } at ${verifying.offer.organization?.name || 'the company'}.`
            : ''
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setVerifying(null)}>
              Cancel
            </Button>
            <Button
              variant={verifying?.verified ? 'success' : 'danger'}
              loading={busyId === verifying?.offer.id}
              onClick={() => verifyLetter(verifying.offer, verifying.verified)}
            >
              {verifying?.verified ? 'Yes, verify' : 'Yes, remove'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">
          {verifying?.verified
            ? 'Check that the letter matches the company, role and CTC. Once verified, the student cannot replace it and the offer counts in placement reports.'
            : 'The offer will no longer count as verified in reports, and the student can replace the letter again.'}
        </p>
      </Modal>

      <Modal
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        size="sm"
        title={confirming?.action === 'accept' ? 'Accept this offer?' : 'Decline this offer?'}
        description={
          confirming
            ? `${confirming.offer.roleTitle || confirming.offer.job?.title} at ${
                confirming.offer.organization?.name || 'the company'
              }, ${formatCtc(confirming.offer.ctc, confirming.offer.currency)}.`
            : ''
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Not yet
            </Button>
            <Button
              variant={confirming?.action === 'accept' ? 'success' : 'danger'}
              loading={busyId === confirming?.offer.id}
              onClick={() => respond(confirming.offer, confirming.action)}
            >
              {confirming?.action === 'accept' ? 'Yes, accept' : 'Yes, decline'}
            </Button>
          </>
        }
      >
        {confirming && (
          <div className="space-y-3 text-sm text-ink-700">
            <p>
              {confirming.action === 'accept'
                ? 'Your answer is final. The company and your placement cell see it straight away, and you will be counted as placed.'
                : 'Your answer is final. The company sees it straight away, and the offer cannot be brought back from here.'}
            </p>
            {confirming.action === 'accept' && acceptConsequences(confirming.offer, policy).length > 0 && (
              <ul className="list-disc space-y-1 pl-5">
                {acceptConsequences(confirming.offer, policy).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {confirming.action === 'accept' && live.some((o) => o.status === 'accepted') && (
              <p className="rounded-xl border border-saffron-500/40 bg-saffron-50 p-3 text-saffron-900">
                You have already accepted another offer. Check with your placement cell before
                accepting a second one.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        size="sm"
        title="Withdraw this offer?"
        description="The student is told immediately, and is counted as unplaced again unless they hold another offer."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={busyId === revoking?.id}
              disabled={reason.trim().length < 3}
              onClick={revoke}
            >
              Withdraw offer
            </Button>
          </>
        }
      >
        <Textarea
          label="Reason"
          required
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Headcount freeze for the quarter"
          help="Sent to the student and recorded against the offer. Required."
        />
      </Modal>
    </PageShell>
  );
};

export default Offers;
